/**
 * Cadastro de setores da empresa (09/10/2026).
 *
 * Antes so dava para CRIAR setor, e so de dentro do "Menu de setores" de uma
 * conexao; renomear, desativar e excluir existiam na API sem tela nenhuma.
 *
 * 🔴 Vinculo com o SGA Desk e PELO NOME: a conversa cai na fila do setor do
 * Desk com o mesmo nome (maiuscula nao importa; acento e espaco no meio
 * importam) ou com esse nome cadastrado como apelido la. O Desk NAO consome o
 * evento `setor.renomeado` — renomear aqui sem renomear la manda as conversas
 * desse setor para a fila geral, sem erro nenhum. A tela avisa isso.
 */
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MenuContexto, type ItemMenu } from '../components/MenuContexto';
import { Botao, Card, Carregando, ErroInline, Tag, TituloTela } from '../components/Sga';
import { api } from '../lib/api';
import type { Setor } from './MenuDeSetores';

function ordenar(lista: Setor[]): Setor[] {
  return [...lista].sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome));
}

export function Setores({ periodo, podeEscrever }: { periodo: string; podeEscrever: boolean }) {
  const [setores, setSetores] = useState<Setor[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [novo, setNovo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [editando, setEditando] = useState<{ id: string; nome: string } | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; setor: Setor } | null>(null);

  const carregar = useCallback(() => {
    setErro(null);
    // Sem `ativos`: o desativado precisa aparecer, senao nao haveria como reativar.
    api
      .get<{ itens: Setor[] }>('/setores')
      .then((d) => setSetores(ordenar(d.itens)))
      .catch((e) => setErro((e as Error).message));
  }, []);
  useEffect(carregar, [carregar]);

  const avisar = (texto: string) => {
    setAviso(texto);
    window.setTimeout(() => setAviso((a) => (a === texto ? null : a)), 4000);
  };

  async function executar(acao: () => Promise<void>) {
    setOcupado(true);
    setErro(null);
    try {
      await acao();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  }

  const criar = () =>
    executar(async () => {
      const nome = novo.trim();
      if (!nome) return;
      // Fica no fim da lista (de 10 em 10, como os setores criados pela integracao).
      const ordem = Math.min(32000, Math.max(0, ...(setores ?? []).map((s) => s.ordem)) + 10);
      await api.post<Setor>('/setores', { nome, ordem });
      setNovo('');
      avisar(`Setor "${nome}" criado. Para cair na fila certa no SGA Desk, ele precisa ter lá um setor com este mesmo nome.`);
      carregar();
    });

  const salvarNome = () =>
    executar(async () => {
      if (!editando) return;
      const atual = setores?.find((s) => s.id === editando.id);
      const nome = editando.nome.trim();
      if (!atual || !nome || nome === atual.nome) {
        setEditando(null);
        return;
      }
      const ok = window.confirm(
        `Renomear "${atual.nome}" para "${nome}"?\n\n`
          + 'O SGA Desk liga o setor PELO NOME. Renomeie também lá (ou cadastre '
          + `"${nome}" como apelido do setor), senão as conversas deste setor vão para a fila geral.`,
      );
      if (!ok) return;
      await api.patch<Setor>(`/setores/${atual.id}`, { nome });
      setEditando(null);
      avisar(`Setor renomeado para "${nome}". Lembre de ajustar o SGA Desk.`);
      carregar();
    });

  const alternarAtivo = (s: Setor) =>
    executar(async () => {
      if (s.ativo) {
        const ok = window.confirm(
          `Desativar o setor "${s.nome}"?\n\nEle sai do menu de escolha dos links e das listas. `
            + 'Conexões que usam este setor continuam funcionando, mas o setor some como opção.',
        );
        if (!ok) return;
      }
      await api.patch<Setor>(`/setores/${s.id}`, { ativo: !s.ativo });
      avisar(s.ativo ? `Setor "${s.nome}" desativado.` : `Setor "${s.nome}" reativado.`);
      carregar();
    });

  const excluir = (s: Setor) =>
    executar(async () => {
      const ok = window.confirm(
        `Excluir o setor "${s.nome}"?\n\nSe ele estiver em uso no menu de algum link, ele não é apagado: fica desativado.`,
      );
      if (!ok) return;
      const r = await api.del<{ apagado: boolean; desativado: boolean }>(`/setores/${s.id}`);
      avisar(r.apagado ? `Setor "${s.nome}" excluído.` : `"${s.nome}" está em uso num menu de link: foi desativado em vez de excluído.`);
      carregar();
    });

  /** Troca a ordem com o vizinho (mesmo PATCH que a API ja tem). */
  const mover = (s: Setor, direcao: -1 | 1) =>
    executar(async () => {
      if (!setores) return;
      const i = setores.findIndex((x) => x.id === s.id);
      const vizinho = setores[i + direcao];
      if (!vizinho) return;
      // Ordens iguais (legado) nao trocariam nada: usa a posicao na lista.
      const a = s.ordem === vizinho.ordem ? (i + direcao + 1) * 10 : vizinho.ordem;
      const b = s.ordem === vizinho.ordem ? (i + 1) * 10 : s.ordem;
      await api.patch<Setor>(`/setores/${s.id}`, { ordem: a });
      await api.patch<Setor>(`/setores/${vizinho.id}`, { ordem: b });
      carregar();
    });

  const itensMenu = (s: Setor): ItemMenu[] => [
    { rotulo: 'Renomear', icone: 'mdi:pencil-outline', onClick: () => setEditando({ id: s.id, nome: s.nome }), desabilitado: !podeEscrever },
    { rotulo: s.ativo ? 'Desativar' : 'Reativar', icone: s.ativo ? 'mdi:pause-circle-outline' : 'mdi:play-circle-outline', onClick: () => void alternarAtivo(s), desabilitado: !podeEscrever },
    { rotulo: 'Excluir', icone: 'mdi:delete-outline', onClick: () => void excluir(s), perigo: true, separadorAntes: true, desabilitado: !podeEscrever },
  ];

  const ativos = useMemo(() => (setores ?? []).filter((s) => s.ativo).length, [setores]);

  return (
    <div className="tela-fixa">
      <TituloTela
        icone="mdi:account-group-outline"
        titulo="Setores"
        contexto={periodo}
        acoes={setores && <span className="hint" style={{ marginTop: 0 }}>{ativos} ativo(s) de {setores.length}</span>}
      />

      <div className="sga-vazio" style={{ alignItems: 'flex-start', textAlign: 'left', gap: 10, padding: '12px 14px', marginBottom: 10 }}>
        <Icon icon="mdi:link-variant" width={20} style={{ flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: 13, lineHeight: 1.5 }}>
          <strong>Vínculo com o SGA Desk: é pelo nome.</strong> A conversa cai na fila do setor do SGA Desk que tiver{' '}
          <strong>o mesmo nome</strong> (maiúsculas não importam; acento importa — "Plantão" ≠ "Plantao") ou esse nome
          cadastrado como apelido lá. Sem correspondente, a conversa vai para a fila geral. Ao <strong>renomear</strong> aqui,
          renomeie também no SGA Desk.
        </span>
      </div>

      <Card
        className="card-fixo tela-fixa__corpo"
        titulo="Setores da empresa"
        acoes={
          podeEscrever && (
            <>
              <input
                className="input"
                style={{ width: 240 }}
                placeholder="Nome do novo setor"
                value={novo}
                maxLength={80}
                onChange={(e) => setNovo(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void criar();
                }}
              />
              <Botao variante="primary" icone="mdi:plus" onClick={() => void criar()} disabled={ocupado || !novo.trim()}>
                Criar setor
              </Botao>
            </>
          )
        }
      >
        {erro && <ErroInline mensagem={erro} aoTentar={carregar} />}
        {aviso && (
          <div className="hint" style={{ margin: '0 0 8px', color: 'var(--sga-primary-text)' }}>
            <Icon icon="mdi:check-circle-outline" width={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
            {aviso}
          </div>
        )}

        <div className="sga-tabela-scroll card-fixo__grid">
          <table className="sga-table sga-table--densa" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Ordem</th>
                <th>Nome</th>
                <th style={{ width: 120 }}>Situação</th>
                <th style={{ width: 170 }}>Horário</th>
                <th style={{ width: 230 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {!setores && !erro && (
                <tr>
                  <td colSpan={5}>
                    <Carregando />
                  </td>
                </tr>
              )}
              {setores?.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <div className="sga-vazio">
                      <Icon icon="mdi:account-group-outline" width={24} />
                      Nenhum setor cadastrado ainda.
                    </div>
                  </td>
                </tr>
              )}
              {setores?.map((s, i) => (
                <tr
                  key={s.id}
                  style={s.ativo ? undefined : { opacity: 0.6 }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMenu({ x: e.clientX, y: e.clientY, setor: s });
                  }}
                >
                  <td>
                    <span style={{ display: 'inline-flex', gap: 2 }}>
                      <Botao variante="link" icone="mdi:chevron-up" title="Subir" onClick={() => void mover(s, -1)} disabled={!podeEscrever || ocupado || i === 0} />
                      <Botao variante="link" icone="mdi:chevron-down" title="Descer" onClick={() => void mover(s, 1)} disabled={!podeEscrever || ocupado || i === setores.length - 1} />
                    </span>
                  </td>
                  <td>
                    {editando?.id === s.id ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <input
                          className="input"
                          autoFocus
                          style={{ width: 220 }}
                          maxLength={80}
                          value={editando.nome}
                          onChange={(e) => setEditando({ id: s.id, nome: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void salvarNome();
                            if (e.key === 'Escape') setEditando(null);
                          }}
                        />
                        <Botao variante="primary" icone="mdi:check" onClick={() => void salvarNome()} disabled={ocupado} />
                        <Botao icone="mdi:close" onClick={() => setEditando(null)} />
                      </span>
                    ) : (
                      <strong>{s.nome}</strong>
                    )}
                  </td>
                  <td>{s.ativo ? <Tag>Ativo</Tag> : <Tag>Desativado</Tag>}</td>
                  <td className="hint" style={{ marginTop: 0 }}>
                    {s.horarios && s.horarios.length > 0 ? `${s.horarios.length} faixa(s) própria(s)` : 'Segue o do link'}
                  </td>
                  <td>
                    {podeEscrever && (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <Botao icone="mdi:pencil-outline" onClick={() => setEditando({ id: s.id, nome: s.nome })} disabled={ocupado}>
                          Renomear
                        </Botao>
                        <Botao
                          icone={s.ativo ? 'mdi:pause-circle-outline' : 'mdi:play-circle-outline'}
                          onClick={() => void alternarAtivo(s)}
                          disabled={ocupado}
                        >
                          {s.ativo ? 'Desativar' : 'Reativar'}
                        </Botao>
                        <Botao variante="danger" icone="mdi:delete-outline" title="Excluir" onClick={() => void excluir(s)} disabled={ocupado} />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {menu && (
        <MenuContexto x={menu.x} y={menu.y} titulo={menu.setor.nome} itens={itensMenu(menu.setor)} aoFechar={() => setMenu(null)} />
      )}
    </div>
  );
}
